import React from "react";
import EmployeeProfileTabHeader from "./EmployeeProfileTabHeader";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import EmployeeProfileEmptyState from "./EmployeeProfileEmptyState";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import { formatDateReadable, getExpirationStatus } from "../../utilities/helpers";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  API_remove_record_from_profile,
  API_get_certificate_types,
} from "../../../../../constants";
import { postRightworkAPI, getRightworkAPI } from "../../utilities/requests";
import { toast } from "react-toastify";
import { Lightbox } from "react-modal-image";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CertificateDrawer from "../../shared/helpers/CertificateDrawer";
import {
  LMS_FILE_TYPES,
  LMS_DATA_TYPES,
  LMS_RECORD_LABELS,
  getDateLabel,
  recordDateText,
  RowShell,
  IconThumb,
} from "./profileRecordHelpers";

function isPdfUrl(url) {
  if (!url) return false;
  const path = url.split("?")[0].toLowerCase();
  return path.endsWith(".pdf");
}

// LMS documentUpload accepts ANY file type (plain type="file" picker), so
// "not a PDF" does not mean "image" - a DOCX/XLSX must get the generic
// document treatment (open in new tab), not a broken <img> + lightbox.
const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".bmp"];
function isImageUrl(url) {
  if (!url) return false;
  const path = url.split("?")[0].toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

// The tab holds two KINDS of file-backed things with different jobs:
// certificates answer "is this person compliant", signed/uploaded documents
// are an archive. They render as separate sections (compact rows, not
// preview cards) so the compliance view is never buried under the archive.
// Data-backed training records live on the Training profile tab.

function EmployeeProfileCertificationsTab(props) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerData, setDrawerData] = React.useState(null);
  const [drawerIsEdit, setDrawerIsEdit] = React.useState(false);

  const [selectedImage, setSelectedImage] = React.useState(null);
  const [showLightbox, setShowLightbox] = React.useState(false);

  // Search + status filtering. Status chips filter to the Certificates
  // section only (the other sections carry no expiration status).
  const [search, setSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all"); // all | attention | valid
  const [collapsedSections, setCollapsedSections] = React.useState({});

  // The employee's own self-profile passes isSelfView so we hide manager-only
  // actions (removing a record from the profile) while still showing everything.
  const isSelfView = !!props.isSelfView;

  const [certificateTypes, setCertificateTypes] = React.useState([]);

  React.useEffect(() => {
    const fetchCertificateTypes = async () => {
      if (!props.homeStore) return;
      try {
        const res = await getRightworkAPI(API_get_certificate_types, {
          store_id: props.homeStore,
        });
        if (res.status === 200) {
          setCertificateTypes(res.data?.redux?.payload?.certificate_types || []);
        }
      } catch (err) {
        console.error("Failed to fetch certificate types:", err);
      }
    };
    fetchCertificateTypes();
  }, [props.homeStore]);

  // LMS record pseudo-types + system defaults as a base; catalog labels (incl.
  // custom types) are merged over the top so custom docs resolve their label.
  const certificateTypeLookup = React.useMemo(() => {
    const base = {
      other: "Other",
      food_permit: "Food Permit",
      liquor_permit: "Liquor Permit",
      break_waiver: "Meal Period Waiver",
      emergency_contact: "Emergency Contact",
      ...LMS_RECORD_LABELS,
    };
    certificateTypes.forEach((t) => {
      base[t.value] = t.label;
    });
    return base;
  }, [certificateTypes]);

  const getFileUrl = (file) => {
    if (typeof file === "string") {
      return file;
    } else if (file && typeof file === "object" && file.url) {
      return file.url;
    }
    return null;
  };

  const getFileName = (file) => {
    if (file && typeof file === "object" && file.name) {
      return file.name;
    }
    return null;
  };

  const handleSave = (certificateUuid, type, expirationDate, file, employeeId, clearFile) => {
    props.post_certificate(
      props.stm_id,
      certificateUuid,
      type,
      expirationDate,
      file,
      clearFile
    );
  };

  const handleDelete = (certificateUuid) => {
    props.delete_certificate(props.stm_id, certificateUuid);
  };

  // Non-destructive: un-pins the record from the profile (keeps the underlying
  // training completion + its Records-tab data intact).
  const handleRemoveLMSRecord = async (recordUuid, recordType) => {
    try {
      const response = await postRightworkAPI(API_remove_record_from_profile, {
        record_type: recordType,
        completion_uuid: recordUuid,
        company_id: props.homeCompany,
      });

      if (response.status === 200) {
        toast.success("Removed from profile");
        if (props.onRefresh) {
          props.onRefresh(false, props.stm_id);
        }
      }
    } catch (error) {
      console.error("Error removing record from profile:", error);
      toast.error("Failed to remove from profile");
    }
  };

  const openFile = (fileUrl) => {
    if (isImageUrl(fileUrl)) {
      setSelectedImage(fileUrl);
      setShowLightbox(true);
    } else {
      // PDFs and any other document type (DOCX/XLSX/...) open in a new tab;
      // the browser downloads what it can't render.
      window.open(fileUrl, "_blank");
    }
  };

  // ---------------------------------------------------------------------------
  // Grouping, search, status filter, sorting
  // ---------------------------------------------------------------------------

  const allRecords = React.useMemo(
    () => props.certificateData || [],
    [props.certificateData]
  );
  const q = search.trim().toLowerCase();

  const matchesSearch = React.useCallback(
    (record) => {
      if (!q) return true;
      const haystack = [
        record.title,
        certificateTypeLookup[record.type],
        record.section_name,
        getFileName(record.file),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    },
    [q, certificateTypeLookup]
  );

  const groups = React.useMemo(() => {
    const certs = [];
    const signedDocs = [];
    allRecords.filter(matchesSearch).forEach((record) => {
      // Data-backed training records render on the Training tab, not here.
      if (LMS_DATA_TYPES.includes(record.type)) return;
      if (LMS_FILE_TYPES.includes(record.type)) signedDocs.push(record);
      else certs.push(record);
    });

    // Certificates: what needs attention first, then by soonest expiration.
    const statusRank = { expired: 0, expiring: 1, valid: 2, none: 3 };
    certs.sort((a, b) => {
      const ra = statusRank[getExpirationStatus(a.expiration_date).status];
      const rb = statusRank[getExpirationStatus(b.expiration_date).status];
      if (ra !== rb) return ra - rb;
      return (a.expiration_date || "9999").localeCompare(b.expiration_date || "9999");
    });
    // Archive: newest first.
    signedDocs.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
    return { certs, signedDocs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRecords, matchesSearch]);

  const attentionCount = groups.certs.filter((c) =>
    ["expired", "expiring"].includes(getExpirationStatus(c.expiration_date).status)
  ).length;

  const visibleCerts =
    statusFilter === "attention"
      ? groups.certs.filter((c) =>
          ["expired", "expiring"].includes(getExpirationStatus(c.expiration_date).status)
        )
      : statusFilter === "valid"
      ? groups.certs.filter((c) =>
          ["valid", "none"].includes(getExpirationStatus(c.expiration_date).status)
        )
      : groups.certs;

  // Status chips describe expiration, which only certificates have — while a
  // status filter is active, focus the view on that section alone.
  const showArchiveSections = statusFilter === "all";

  const toggleSection = (key) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // ---------------------------------------------------------------------------
  // Row + section building blocks
  // ---------------------------------------------------------------------------

  const StatusChip = ({ record }) => {
    const { status } = getExpirationStatus(record.expiration_date);
    if (status === "expired") {
      return (
        <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
          Expired
        </span>
      );
    }
    if (status === "expiring") {
      return (
        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
          Expires soon
        </span>
      );
    }
    if (status === "valid") {
      return (
        <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
          Valid
        </span>
      );
    }
    return null;
  };

  const FileThumb = ({ fileUrl }) =>
    isPdfUrl(fileUrl) ? (
      <button
        type="button"
        onClick={() => openFile(fileUrl)}
        className="w-11 h-11 shrink-0 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center hover:bg-red-100 transition-colors"
        title="Open PDF"
      >
        <PictureAsPdfIcon style={{ fontSize: 20 }} className="text-red-500" />
      </button>
    ) : isImageUrl(fileUrl) ? (
      <button
        type="button"
        onClick={() => openFile(fileUrl)}
        className="w-11 h-11 shrink-0 rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
        title="View image"
      >
        <img src={fileUrl} alt="Document" className="w-full h-full object-cover" />
      </button>
    ) : (
      <button
        type="button"
        onClick={() => openFile(fileUrl)}
        className="w-11 h-11 shrink-0 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center hover:bg-slate-100 transition-colors"
        title="Open document"
      >
        <DescriptionIcon style={{ fontSize: 20 }} className="text-slate-500" />
      </button>
    );

  const renderCertificateRow = (certificate) => {
    const fileUrl = getFileUrl(certificate.file);
    const { status } = getExpirationStatus(certificate.expiration_date);
    const title =
      certificate.title ||
      (certificate.type ? certificateTypeLookup[certificate.type] : "Untitled Document");
    const dateText = certificate.expiration_date
      ? `${status === "expired" ? "Expired" : "Expires"} ${formatDateReadable(certificate.expiration_date)}`
      : "No expiration date";

    return (
      <RowShell
        key={certificate.uuid}
        leading={
          fileUrl ? (
            <FileThumb fileUrl={fileUrl} />
          ) : (
            <div className="w-11 h-11 shrink-0 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
              <DescriptionIcon className="text-slate-500" style={{ fontSize: 20 }} />
            </div>
          )
        }
        actions={
          <>
            {fileUrl && (
              <Tooltip title="View document">
                <IconButton size="small" onClick={() => openFile(fileUrl)}>
                  <VisibilityIcon style={{ fontSize: 17 }} className="text-slate-500" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit document">
              <IconButton
                size="small"
                onClick={() => {
                  setDrawerData(certificate);
                  setDrawerIsEdit(true);
                  setDrawerOpen(true);
                }}
              >
                <EditIcon style={{ fontSize: 16 }} className="text-slate-500" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete document">
              <IconButton
                size="small"
                onClick={() => {
                  if (
                    !window.confirm(
                      "Are you sure you want to delete this document? This action cannot be undone."
                    )
                  ) {
                    return;
                  }
                  props.delete_certificate(props.stm_id, certificate.uuid);
                }}
              >
                <DeleteIcon style={{ fontSize: 16 }} className="text-red-500" />
              </IconButton>
            </Tooltip>
          </>
        }
      >
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{title}</p>
          <StatusChip record={certificate} />
        </div>
        <p className="text-xs text-slate-500 truncate mt-0.5">{dateText}</p>
      </RowShell>
    );
  };

  const renderSignedDocRow = (record) => {
    const fileUrl = getFileUrl(record.file);
    const title = record.section_name || record.title || certificateTypeLookup[record.type];
    return (
      <RowShell
        key={record.uuid}
        leading={fileUrl ? <FileThumb fileUrl={fileUrl} /> : <IconThumb type={record.type} />}
        actions={
          <>
            {fileUrl && (
              <Tooltip title="View document">
                <IconButton size="small" onClick={() => window.open(fileUrl, "_blank")}>
                  <VisibilityIcon style={{ fontSize: 17 }} className="text-slate-500" />
                </IconButton>
              </Tooltip>
            )}
            {!isSelfView && (
              <Tooltip title="Remove from profile">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (
                      !window.confirm(
                        "Remove this record from the employee's profile? The training completion itself is kept."
                      )
                    ) {
                      return;
                    }
                    handleRemoveLMSRecord(record.uuid, record.type);
                  }}
                >
                  <DeleteIcon style={{ fontSize: 16 }} className="text-red-500" />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      >
        <p className="text-sm font-semibold text-slate-900 truncate">{title}</p>
        <p className="text-xs text-slate-500 truncate mt-0.5">
          {certificateTypeLookup[record.type]} · {getDateLabel(record.type)}{" "}
          {recordDateText(record)}
        </p>
      </RowShell>
    );
  };

  const Section = ({ sectionKey, icon: SectionIcon, title, records, renderRow }) => {
    if (records.length === 0) return null;
    const isCollapsed = !!collapsedSections[sectionKey];
    return (
      <div className="mb-5">
        <button
          type="button"
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center gap-2 px-1 py-1.5 group"
        >
          {isCollapsed ? (
            <ChevronRightIcon style={{ fontSize: 18 }} className="text-slate-400" />
          ) : (
            <ExpandMoreIcon style={{ fontSize: 18 }} className="text-slate-400" />
          )}
          <SectionIcon style={{ fontSize: 17 }} className="text-slate-500" />
          <span className="text-sm font-semibold text-slate-700 group-hover:text-slate-900">
            {title}
          </span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
            {records.length}
          </span>
        </button>
        {!isCollapsed && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100 overflow-hidden">
            {records.map(renderRow)}
          </div>
        )}
      </div>
    );
  };

  const anyVisible =
    visibleCerts.length > 0 ||
    (showArchiveSections && groups.signedDocs.length > 0);

  return (
    <>
      {showLightbox && selectedImage && (
        <div className="absolute w-screen h-screen z-50">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <EmployeeProfileTabHeader
          icon={DescriptionIcon}
          title="Documents"
          subtitle="Certificates and signed documents"
          actionLabel="Add Certificate"
          actionIcon={AddIcon}
          onAction={() => {
            setDrawerIsEdit(false);
            setDrawerData(null);
            setDrawerOpen(true);
          }}
        />

        {allRecords.length > 0 ? (
          <>
            {/* Toolbar: search + status filter */}
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <SearchIcon
                  style={{ fontSize: 18 }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search documents…"
                  className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setStatusFilter("all")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    statusFilter === "all"
                      ? "bg-violet-600 text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter("attention")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    statusFilter === "attention"
                      ? "bg-red-600 text-white"
                      : attentionCount > 0
                      ? "bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Needs attention{attentionCount > 0 ? ` (${attentionCount})` : ""}
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter("valid")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    statusFilter === "valid"
                      ? "bg-emerald-600 text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Valid
                </button>
              </div>
            </div>

            {anyVisible ? (
              <>
                <Section
                  sectionKey="certs"
                  icon={WorkspacePremiumIcon}
                  title="Certificates"
                  records={visibleCerts}
                  renderRow={renderCertificateRow}
                />
                {showArchiveSections && (
                  <>
                    {/* Sections are named by CONTENT, never origin: an
                        origin-based name ("Training Documents") implies
                        training-earned certificates belong there, but certs
                        must stay unified in the compliance section above
                        regardless of how they were obtained. */}
                    <Section
                      sectionKey="signed"
                      icon={DrawOutlinedIcon}
                      title="Signed & Uploaded"
                      records={groups.signedDocs}
                      renderRow={renderSignedDocRow}
                    />
                  </>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 py-10 text-center">
                <p className="text-sm text-slate-500">
                  No documents match{q ? ` "${search.trim()}"` : " this filter"}.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                  }}
                  className="mt-2 text-sm font-medium text-violet-700 hover:text-violet-900"
                >
                  Clear filters
                </button>
              </div>
            )}
          </>
        ) : (
          <EmployeeProfileSectionCard bodyClassName="">
            <EmployeeProfileEmptyState
              icon={DescriptionIcon}
              title="No records yet"
              description="Certificates and signed documents will appear here."
              action={
                <button
                  onClick={() => {
                    setDrawerIsEdit(false);
                    setDrawerData(null);
                    setDrawerOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                >
                  <AddIcon style={{ fontSize: "16px" }} />
                  <span>Add Certificate</span>
                </button>
              }
            />
          </EmployeeProfileSectionCard>
        )}
      </div>

      <CertificateDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        certificateData={drawerData}
        isEdit={drawerIsEdit}
        onSave={handleSave}
        onDelete={handleDelete}
        certificateTypes={certificateTypes}
      />
    </>
  );
}

export default EmployeeProfileCertificationsTab;
